import React, { type JSX } from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { IRadioChoiceField } from "../../models/formFields";
import { FormInputFieldWrapper } from "../FormInputFieldWrapper";
import {
    FORM_RADIO_OPTIONS_CLASS_NAME,
    FORM_RADIO_OPTION_CLASS_NAME,
    FORM_RADIO_OPTION_LABEL_CHECKED_CLASS_NAME,
    FORM_RADIO_OPTION_LABEL_CLASS_NAME,
} from "../classNames";
import { useFormField } from "../hooks/useFormField";

import styles from "../../styles/composable/forms.module.scss";

const buildOptionHelpText = (
    str: string | JSX.Element | undefined,
): React.ReactNode => {
    if (!str) {
        return null;
    }

    return (
        <div
            className={concatClassNames([
                styles.formRadioOptionHelpText,
                "form__radio-option__help-text",
            ])}
        >
            {str}
        </div>
    );
};

export const FormRadioSelect = <T extends string>(
    props: IRadioChoiceField<T>,
): React.ReactNode => {
    const { type = "radio-choice", choices, legend } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    const inputProps = field.inputProps;

    const options = (choices || []).map((option) => {
        const isChecked = inputProps.value === option.value;
        const helpText = buildOptionHelpText(option.helpText);
        const optionClasses = concatClassNames([
            FORM_RADIO_OPTION_CLASS_NAME,
            isChecked ? "form__radio-option--checked" : null,
        ]);
        const optionLabelClasses = concatClassNames([
            FORM_RADIO_OPTION_LABEL_CLASS_NAME,
            isChecked ? FORM_RADIO_OPTION_LABEL_CHECKED_CLASS_NAME : null,
        ]);
        return (
            <div className={optionClasses} key={option.value}>
                <label
                    className={optionLabelClasses}
                    htmlFor={`${props.id}-${option.value}`}
                >
                    <input
                        type="radio"
                        name={props.name}
                        id={`${props.id}-${option.value}`}
                        value={option.value}
                        checked={isChecked}
                        disabled={option.disabled}
                        required={field.isRequired || undefined}
                        aria-describedby={inputProps["aria-describedby"]}
                        aria-invalid={inputProps["aria-invalid"]}
                        onChange={inputProps.onChange}
                    />
                    <span>
                        {option.label}
                        {helpText}
                        {option.extraText}
                    </span>
                </label>
            </div>
        );
    });

    const fieldName = inputProps.name.split("-")[0];
    const optionsLegendClasses =
        fieldName === "rating" || fieldName === "bottomline"
            ? "ada-screenreader-only"
            : undefined;

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <fieldset
                id={inputProps.id}
                className={FORM_RADIO_OPTIONS_CLASS_NAME}
                aria-required={field.isRequired || undefined}
            >
                <legend className={optionsLegendClasses}>{legend}</legend>
                {options}
            </fieldset>
        </FormInputFieldWrapper>
    );
};
